import {RRLogisticType} from "./RRLogisticType";
import {Button, Col, Modal, Row, Spinner} from "react-bootstrap";
import {useEffect, useMemo, useState, useRef} from "react";
import ReactDatePicker from "react-datepicker";
import {CancelIconButton, CheckIconButton} from "../../components/IconButtons";
import {collection, doc, query, where} from "firebase/firestore";
import {appFirestore} from "../../../index";
import {dateTimeToString, timeDiffToString} from "../../tools";
import {trucksFBConverter} from "../trucks/TrucksFBConverter";
import {useCollectionData, useDocumentData} from "react-firebase-hooks/firestore";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClockRotateLeft} from "@fortawesome/free-solid-svg-icons";
import {saveTransportMutation, transportMutationError} from "./transportMutations";
import {
    LOGISTICS_CHANGE_LOG_COLLECTION,
    LogisticsChangeLogType,
    LogisticsDriverChangeLogType,
} from "./LogisticsDriverChangeLogType";
import {logisticsDriverChangeLogFBConverter} from "./LogisticsDriverChangeLogFBConverter";

interface LogisticReportDetailsProps {
    logistic: RRLogisticType,
    onDone: () => void,
}

type LogisticsReportChangeField = Extract<
    LogisticsChangeLogType,
    "REPORT_START_DATE" | "REPORT_END_DATE" | "START_KM" | "END_KM" | "REPORT_NOTES"
>;

type LogisticsReportHistoryChange = LogisticsDriverChangeLogType & {
    changeType: LogisticsReportChangeField;
};

function hasPreviousValueForHistory(value: string): boolean {
    const trimmedValue = value.trim();

    return trimmedValue.length > 0 && trimmedValue !== "—" && trimmedValue !== "(празно)";
}

function reportChangeTypeLabel(changeType: LogisticsReportChangeField): string {
    switch (changeType) {
        case "REPORT_START_DATE":
            return "Начална дата / час";
        case "REPORT_END_DATE":
            return "Крайна дата / час";
        case "START_KM":
            return "Начален километраж";
        case "END_KM":
            return "Краен километраж";
        case "REPORT_NOTES":
            return "Бележки";
    }
}

function isReportHistoryChange(change: LogisticsDriverChangeLogType): change is LogisticsReportHistoryChange {
    return change.changeType === "REPORT_START_DATE" ||
        change.changeType === "REPORT_END_DATE" ||
        change.changeType === "START_KM" ||
        change.changeType === "END_KM" ||
        change.changeType === "REPORT_NOTES";
}

type LogisticReportHistoryDialogProps = {
    changes: LogisticsReportHistoryChange[];
    onHide: () => void;
    show: boolean;
};

function LogisticReportHistoryDialog({changes, onHide, show}: LogisticReportHistoryDialogProps) {
    return <Modal backdrop="static" centered keyboard={false} onHide={onHide} show={show} size="lg">
        <Modal.Header closeButton>
            <Modal.Title><FontAwesomeIcon className="me-2" icon={faClockRotateLeft}/>История на промените</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <table className="table table-sm table-striped mb-0">
                <thead>
                <tr>
                    <th>Дата / час</th>
                    <th>Потребител</th>
                    <th>Променено поле</th>
                    <th>Предишна стойност</th>
                </tr>
                </thead>
                <tbody>
                {changes
                    .filter(change => hasPreviousValueForHistory(change.previousValue))
                    .map(change => <tr key={change.id}>
                    <td>{dateTimeToString(change.changedAt)}</td>
                    <td>{change.changedByName || "Неизвестен потребител"}</td>
                    <td>{reportChangeTypeLabel(change.changeType)}</td>
                    <td>{change.previousValue}</td>
                </tr>)}
                </tbody>
            </table>
        </Modal.Body>
    </Modal>;
}

export function LogisticReportDetailsContent({logistic, onDone}: LogisticReportDetailsProps) {
    const [editedLogistic, setEditedLogistic] = useState<RRLogisticType>({...logistic});
    const [showChangeHistory, setShowChangeHistory] = useState(false);
    const pendingWrite = useRef(false);
    const [saving, setSaving] = useState(false);

    const changeHistoryQuery = useMemo(() => query(
        collection(appFirestore, LOGISTICS_CHANGE_LOG_COLLECTION),
        where("logisticId", "==", logistic.id ?? "")
    ).withConverter(logisticsDriverChangeLogFBConverter), [logistic.id]);
    const [changeHistory, loadingChangeHistory] = useCollectionData(changeHistoryQuery);
    const sortedChangeHistory = useMemo(() => [...(changeHistory ?? [])].sort((first, second) =>
        second.changedAt.getTime() - first.changedAt.getTime()
    ), [changeHistory]);
    const reportChangeHistory = sortedChangeHistory.filter(isReportHistoryChange);
    const hasVisibleChangeHistory = reportChangeHistory.some(change => hasPreviousValueForHistory(change.previousValue));

    const truckDocRef = useMemo(() => (
        (logistic.truck && logistic.truck.id)
            ? doc(collection(appFirestore, "trucks"), logistic.truck.id).withConverter(trucksFBConverter)
            : null
    ), [logistic.truck]);
    const [truck] = useDocumentData(truckDocRef);

    useEffect(() => {
        const newLogistic = {...logistic};
        if (!newLogistic.reportStartDate) {
            newLogistic.reportStartDate = newLogistic.startDate;
        }
        if (!newLogistic.reportEndDate) {
            newLogistic.reportEndDate = newLogistic.endDate;
        }

        if (!newLogistic.startKm) {
            newLogistic.startKm = truck?.lastKm ?? 0;
        }
        setEditedLogistic(newLogistic);
    }, [logistic, truck]);


    async function save() {
        if (pendingWrite.current) return;
        pendingWrite.current = true;
        setSaving(true);
        try {
            if (editedLogistic.id) {
                await saveTransportMutation({
                    action: "reportSave",
                    logisticId: editedLogistic.id,
                    expectedRevision: logistic.revision ?? 0,
                    source: logistic,
                    report: {
                        reportStartDate: editedLogistic.reportStartDate,
                        reportEndDate: editedLogistic.reportEndDate,
                        startKm: editedLogistic.startKm,
                        endKm: editedLogistic.endKm,
                        reportNotes: editedLogistic.reportNotes
                    }
                });
            }

            onDone();
        } catch (e: unknown) {
            alert("Грешка при запазване на логистиката: " + transportMutationError(e));
        } finally {
            pendingWrite.current = false;
            setSaving(false);
        }
    }

    return (<>
            <Row className={"mb-2"}>
                <Col xs={4}><h5>Шофьор:</h5></Col>
                <Col><h5>{editedLogistic.driverName}</h5></Col>
            </Row>
            {
                !editedLogistic.workInStore &&
                <Row className={"mb-2"}>
                    <Col xs={4}><h5>Курс:</h5></Col>
                    <Col><h5>{editedLogistic.startPoint} {">"} {editedLogistic.endPoint}</h5></Col>
                </Row>
            }
            {
                editedLogistic.workInStore &&
                <Row className={"mb-2"}>
                    <Col xs={4}><h5>Място:</h5></Col>
                    <Col>СКЛАД</Col>
                </Row>
            }

            <Row className={"mb-2"}>
                <Col xs={4}>Начална дата / час:</Col>
                <Col>
                    <ReactDatePicker
                        selected={editedLogistic.reportStartDate}
                        onChange={(date: Date | null) => {
                            if (date) {
                                setEditedLogistic({
                                    ...editedLogistic,
                                    reportStartDate: date,
                                    reportEndDate: editedLogistic.reportEndDate && date > editedLogistic.reportEndDate ? date : editedLogistic.reportEndDate
                                });
                            }
                        }}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        dateFormat="dd.MM.yyyy HH:mm"
                        className="form-control"
                    />
                </Col>
            </Row>

            <Row className={"mb-4"}>
                <Col xs={4}>Начален километраж:</Col>
                <Col>
                    <input
                        type="number"
                        className="form-control"
                        value={editedLogistic.startKm || ""}
                        onChange={(e) => {
                            const value = e.target.value ? Number(e.target.value) : null;
                            setEditedLogistic({
                                ...editedLogistic,
                                startKm: value
                            });
                        }}
                    />
                </Col>
            </Row>

            <Row className={"mb-2"}>
                <Col xs={4}>Крайна дата / час:</Col>
                <Col>
                    <ReactDatePicker
                        selected={editedLogistic.reportEndDate}
                        onChange={(date: Date | null) => {
                            if (date) {
                                setEditedLogistic({
                                    ...editedLogistic,
                                    reportEndDate: date
                                });
                            }
                        }}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        dateFormat="dd.MM.yyyy HH:mm"
                        className="form-control"
                        minDate={editedLogistic?.reportStartDate}
                    />
                </Col>
            </Row>

            <Row className={"mb-4"}>
                <Col xs={4}>Краен километраж:</Col>
                <Col>
                    <input
                        type="number"
                        className="form-control"
                        value={editedLogistic.endKm || ""}
                        onChange={(e) => {
                            const value = e.target.value ? Number(e.target.value) : null;
                            setEditedLogistic({
                                ...editedLogistic,
                                endKm: value
                            });
                        }}
                    />
                </Col>
            </Row>

            <Row className={"mb-2"}>
                <Col xs={4}>Бележки:</Col>
                <Col>
                            <textarea
                                className="form-control"
                                rows={3}
                                value={editedLogistic.reportNotes}
                                onChange={(e) => {
                                    setEditedLogistic({
                                        ...editedLogistic,
                                        reportNotes: e.target.value
                                    });
                                }}
                            />
                </Col>
            </Row>

            <Row className={"border-0 border-top pt-3"}>
                <Col className={"fw-bold pt-2"}>
                    Работни часове: &nbsp;
                    {
                        timeDiffToString(
                            editedLogistic.reportStartDate,
                            editedLogistic.reportEndDate,
                            0
                        )
                    }
                </Col>
                <Col className={"text-end"}>
                    {loadingChangeHistory ? <Spinner animation="border" size="sm"/> : null}
                    {hasVisibleChangeHistory ? <>
                        <Button
                            onClick={() => setShowChangeHistory(true)}
                            title="История на промените"
                            variant="outline-secondary"
                        >
                            <FontAwesomeIcon icon={faClockRotateLeft}/>
                        </Button>&nbsp;
                    </> : null}
                    <CheckIconButton title="Запиши" disabled={saving} onClick={save}/>&nbsp;
                    <CancelIconButton onClick={onDone}/>
                </Col>
            </Row>
            <LogisticReportHistoryDialog
                changes={reportChangeHistory}
                onHide={() => setShowChangeHistory(false)}
                show={showChangeHistory}
            />
        </>
    );
}

export default function LogisticReportDetails({logistic, onDone}: LogisticReportDetailsProps) {
    return (
        <>
            <Modal show={true} onHide={onDone} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Отчитане на логистика</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <LogisticReportDetailsContent
                        logistic={logistic}
                        onDone={onDone}
                    />
                </Modal.Body>
            </Modal>
        </>
    );
}
